import type React from 'react'
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import PersonalInformation from '../components/Profile-Setting-Sections/personal-information'
import CompanyInfo from '../components/Profile-Setting-Sections/company-info'
import { useAppSelector, useAppDispatch } from '../app/hooks'
import { ensureCurrentUser } from '../features/auth/authSlice'

const ProfileSetting: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabFromUrl = searchParams.get('tab') as 'personal' | 'company' | null
  const [activeTab, setActiveTab] = useState<'personal' | 'company'>(tabFromUrl || 'personal')
  const [userGroups, setUserGroups] = useState<string[]>([])
  const dispatch = useAppDispatch()
  const accessToken = useAppSelector((state) => state.auth.accessToken)

  // Initialize tab from URL on mount
  useEffect(() => {
    if (tabFromUrl && (tabFromUrl === 'personal' || tabFromUrl === 'company')) {
      setActiveTab(tabFromUrl)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadUserData = async () => {
      if (!accessToken) return

      try {
        const ensuredUser = await dispatch(ensureCurrentUser()).unwrap()
        const groups = ensuredUser.groups ?? []
        setUserGroups(groups)
      } catch (error) {
        console.error('Failed to fetch current user:', error)
      }
    }

    loadUserData()
  }, [accessToken, dispatch])

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true })
    }
  }, [activeTab, setSearchParams])

  // Reset to personal tab if company tab is active but user is not admin
  useEffect(() => {
    if (activeTab === 'company' && !userGroups.includes("company_admin") && userGroups.length > 0) {
      setActiveTab('personal')
    }
  }, [userGroups, activeTab])

  return (
    <div className="p-4">
      <div className="w-full mx-auto bg-white rounded-md border border-gray-200 shadow-sm">
        {/* Tab Navigation */}
        <div className="flex border-b p-6 gap-5 border-gray-200">
          <button
            onClick={() => setActiveTab('personal')}
            // className={`border border-gray-300 px-2 py-1 rounded-md drop-shadow-md font-medium transition-colors ${activeTab === 'personal'
            className={`border border-gray-200 px-2 py-1 rounded-md drop-shadow-sm font-medium transition-colors ${activeTab === 'personal'
              ? 'text-gray-900 font-bold shadow-[0_0_6px_0px_rgba(0,0,0,0.2)]'
              : 'text-[#ADA7A7] hover:text-gray-700'
              }`}
          >
            Personal information
          </button>
          {(userGroups.length === 0 || userGroups.includes("company_admin")) && (
            <button
              onClick={() => setActiveTab('company')}
              className={`border-2 border-gray-200 px-2 py-1 rounded-md drop-shadow-sm font-medium transition-colors ${activeTab === 'company'
                ? 'text-gray-900 font-bold shadow-[0_0_6px_0px_rgba(0,0,0,0.2)]'
                : 'text-[#ADA7A7] hover:text-gray-700'
                }`}
            >
              Company info
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'personal' && <PersonalInformation />}
          {activeTab === 'company' && (userGroups.length === 0 || userGroups.includes("company_admin")) && <CompanyInfo />}
        </div>
      </div>
    </div>
  )
}

export default ProfileSetting
